import config from 'config'
import { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'

import PanelSwitchField from 'shared/components/PanelSwitchField'
import SecondLevelTitle from 'shared/components/Tools/components/PanelSecondLevelTitle'
import ChiliVariables, {
  VariableTypes
} from 'shared/components/Tools/services/ChiliVariables'
import Button from 'shared/ui/Button_2'
import Flex from 'shared/ui/Flex'
import CustomScrollbars from 'shared/ui/SimpleBar'
import TabBar from 'shared/ui/TabBar'

import { FooterContainer } from './Style'
import LibraryTab from './tabs/LibraryTab'
import UploadsTab from './tabs/UploadsTab'
import { BackgroundTabProps } from './types'

const variablesApi = ChiliVariables.getInstance()

type ChangeBackgroundPanelProps = {
  variableName: string
  withResetButton?: boolean
  onBackButtonClick: () => void
  showDeleteBackgroundButton?: boolean
}

const ChangeBackgroundPanel: React.FC<ChangeBackgroundPanelProps> = ({
  variableName,
  onBackButtonClick,
  showDeleteBackgroundButton = false
}) => {
  const { formatMessage } = useIntl()

  const [currentTabIndex, setCurrentTabIndex] = useState(0)
  const [isBackgroundHidden, setIsBackgroundHidden] = useState(false)

  const TABS_MAP: Array<{
    name: string
    component: React.FC<BackgroundTabProps>
    props: BackgroundTabProps
  }> = [
    {
      name: formatMessage({
        id: `Project.settings.templates.backgrounds.modal.tabs.library`
      }),
      component: LibraryTab,
      props: {
        variableName,
        isBackgroundHidden
      }
    },
    {
      name: formatMessage({
        id: `Project.settings.templates.backgrounds.modal.tabs.uploads`
      }),
      component: UploadsTab,
      props: {
        variableName,
        isBackgroundHidden
      }
    }
  ]

  useEffect(() => {
    const fetchVar = async () => {
      await variablesApi.getVariable(
        config.themes.variablesKeys.backgroundImage
      )
    }

    fetchVar()
  }, [variablesApi.isInitialized])

  return (
    <CustomScrollbars
      fixedHeader={
        <SecondLevelTitle
          titleId="Panel.title.changeBackground"
          onClick={onBackButtonClick}
          margin={false}
        />
      }
      fixedFooter={
        <FooterContainer>
          {showDeleteBackgroundButton ? (
            <Button
              textId="Panel.startEditing.background.delete"
              intent="destructive"
              size="md"
              minWidth={242}
              onPress={() =>
                variablesApi.setVariableValue(variableName, '', {
                  type: VariableTypes.chiliImage
                })
              }
            />
          ) : (
            <PanelSwitchField
              labelId="Panel.startEditing.background.hide"
              value={isBackgroundHidden}
              onChange={value => {
                setIsBackgroundHidden(value)
                if (value === true) {
                  variablesApi.setVariableValue(variableName, '', {
                    type: VariableTypes.chiliImage
                  })
                }
              }}
            />
          )}
        </FooterContainer>
      }
    >
      <TabBar
        tabs={TABS_MAP.map(({ name }) => name)}
        activeIndex={currentTabIndex}
        onChange={setCurrentTabIndex}
        width={242}
      />
      <Flex>
        {(() => {
          const CurrentComponent = TABS_MAP[currentTabIndex].component
          const props = TABS_MAP[currentTabIndex].props
          return <CurrentComponent {...props} />
        })()}
      </Flex>
    </CustomScrollbars>
  )
}

export default ChangeBackgroundPanel
